
<template>
	<div class="widget-card">
		<h2 v-if="widget" class="text-center">{{ widget.dashboardwidgets_title }}</h2>

		<LoadingSpinner v-if="loading" />
		<template v-else>
			<!-- Navigation Tableau / France / International -->
			<div class="d-flex justify-content-end mb-3">
				<b-btn-group size="sm">
					<b-button :variant="view === 'table' ? 'primary' : 'outline-secondary'" @click.prevent="view = 'table'">
						<font-awesome-icon :icon="['far', 'table']" /> {{ $t('cards.widget.contract_map_table') }}
					</b-button>
					<b-button :variant="view === 'france' ? 'primary' : 'outline-secondary'" @click.prevent="view = 'france'; initMapFrance()">
						<font-awesome-icon :icon="['far', 'map-marker-alt']" /> {{ $t('cards.widget.contract_map_tab_france') }}
					</b-button>
					<b-button :variant="view === 'international' ? 'primary' : 'outline-secondary'" @click.prevent="view = 'international'; initMapInternational()">
						<font-awesome-icon :icon="['far', 'globe']" /> {{ $t('cards.widget.contract_map_tab_international') }}
					</b-button>
				</b-btn-group>
			</div>

			<!-- Vue Tableau -->
			<template v-if="view === 'table'">
				<b-table
					:items="tableRows"
					:fields="tableFields"
					small
					bordered
					sort-icon-left
				>
				</b-table>
				<p v-if="!tableRows.length" class="text-center text-muted">{{ $t('cards.widget.contract_map_empty') }}</p>
			</template>

			<!-- Vue Carte France -->
			<div v-show="view === 'france'" ref="mapContainerFrance" style="height: 420px; border-radius: 6px; overflow: hidden; background: #30415E;" />

			<!-- Vue Carte International -->
			<div v-show="view === 'international'" ref="mapContainerInternational" style="height: 420px; border-radius: 6px; overflow: hidden; background: #30415E;" />
		</template>

	</div>
</template>

<script type="text/javascript">
import Common from '@/assets/js/common.js'
import DashboardMixin from '@/mixins/Dashboard.js'
import deptsJson from '@/assets/lang/fr/departements.json'
import Countries from '@/assets/lang/countries/countries.js'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import france_departements from '@/assets/js/france_departements.js'
import world_countries from '@/assets/js/world_countries.js'

export default {
	name: 'WidgetContractMap',
	mixins: [DashboardMixin],
	props: ['widget'],
	data() {
		return {
			loading: true,
			stats: [],
			view: 'table', // 'table', 'france', 'international'

			// Table
			tableFields: [],
			tableRows: [],

			// France
			leafletMapFrance: null,
			geoLayerFrance: null,
			statsFrance: [],

			// International
			leafletMapInternational: null,
			geoLayerInternational: null,
			statsInternational: [],
			franceInternational: null,
			countryLabelByIso: {},
		}
	},

	mounted() {
		this.loadData()
	},

	methods: {
		async loadData() {
			this.loading = true

			// Construit la map ISO → libellé pays dans la langue de l'application.
			// Le dictionnaire contient des doublons (ex : « Belgique » id 21 et « België »
			// id 290 pointent tous deux sur "BE"). On garde le premier libellé rencontré
			// (id le plus bas = libellé canonique) pour afficher « Belgique » et non « België ».
			const lang = this.$store.state.user.lang
			const countries = Countries[lang]
			const countryLabelByIso = {}
			for (const id of Object.keys(countries.iso)) {
				const iso = countries.iso[id]
				if (!countryLabelByIso[iso]) {
					countryLabelByIso[iso] = countries.label[id]
				}
			}
			this.countryLabelByIso = countryLabelByIso

			// Récupère la configuration du widget
			const config = this.widget.dashboardwidgets_config

			// La saison est obligatoire : sans elle, on n'appelle pas l'API (évite une requête
			// non filtrée sur tous les contrats) et on affiche l'état vide.
			const data = await this.getContractStatsMap({
				saison: config.saison,
				etalon: config.etalon || null,
			})

			if (!data || !data.stats) {
				this.stats = []
				this.statsFrance = []
				this.statsInternational = []
				this.franceInternational = null
				this.tableRows = []
				this.loading = false
				return
			}

			this.stats = data.stats

			// Sépare les stats France, International et sans lieu de monte
			this.statsFrance = data.stats.filter(s => s.dept_code)
			this.statsInternational = data.stats.filter(s => !s.dept_code && s.country_code)
			const statsNoLocation = data.stats.filter(s => !s.dept_code && !s.country_code)

			// Agrège tous les contrats France en une entrée unique pour la carte internationale
			// (afin que la France soit colorée si elle a des contrats). On lit le CA brut
			// avant le formatage de la devise effectué juste après.
			const francePropaleCount = this.statsFrance.reduce((sum, s) => sum + s.propale.count, 0)
			const franceSigneCount = this.statsFrance.reduce((sum, s) => sum + s.signe.count, 0)
			const francePropaleCA = this.statsFrance.reduce((sum, s) => sum + (s.propale.ca || 0), 0)
			const franceSigneCA = this.statsFrance.reduce((sum, s) => sum + (s.signe.ca || 0), 0)

			// Formate la devise pour chaque stat (les montants sont stockés en centimes)
			for (const stat of data.stats) {
				stat.propale.ca = await Common.priceFormat((stat.propale.ca || 0) / 100, 'EUR', true)
				stat.signe.ca = await Common.priceFormat((stat.signe.ca || 0) / 100, 'EUR', true)
			}

			// Entrée France pour la carte internationale (CA déjà formaté)
			this.franceInternational = (francePropaleCount + franceSigneCount) ? {
				country_code: 'FR',
				propale: { count: francePropaleCount, ca: await Common.priceFormat(francePropaleCA / 100, 'EUR', true) },
				signe: { count: franceSigneCount, ca: await Common.priceFormat(franceSigneCA / 100, 'EUR', true) },
			} : null

			// Calcule les totaux pour le tableau résumé
			const franceCount = this.statsFrance.reduce((sum, s) => sum + s.propale.count + s.signe.count, 0)
			const abroadCount = this.statsInternational.reduce((sum, s) => sum + s.propale.count + s.signe.count, 0)
			const noLocationCount = statsNoLocation.reduce((sum, s) => sum + s.propale.count + s.signe.count, 0)

			// Construit le tableau résumé
			const fields = [
				{ key: 'france_count', label: this.$t('cards.widget.contract_map_france') },
				{ key: 'abroad_count', label: this.$t('cards.widget.contract_map_abroad') },
				{ key: 'no_location_count', label: this.$t('cards.widget.contract_map_no_location') },
			]
			this.tableFields = fields

			this.tableRows = data.stats.length ? [{ france_count: franceCount, abroad_count: abroadCount, no_location_count: noLocationCount }] : []

			this.loading = false
		},

		getDeptLabel(code) {
			if (!code) return this.$t('cards.widget.contract_map_unknown')
			return deptsJson[code] || code
		},

		getCountryLabel(code) {
			if (!code) return this.$t('cards.widget.contract_map_unknown')
			return this.countryLabelByIso[code] || code
		},

		async initMapFrance() {
			await this.$nextTick()
			if (this.leafletMapFrance) return this.leafletMapFrance.invalidateSize()

			const container = this.$refs.mapContainerFrance
			if (!container) return

			const map = L.map(container, { zoomControl: true, attributionControl: false })

			this.leafletMapFrance = map
			const geoLayer = await this.addChoroplethFrance(map)
			const bounds = geoLayer.getBounds()
			map.fitBounds(bounds)
			map.setMinZoom(map.getZoom())
			map.setMaxBounds(bounds)
		},

		async initMapInternational() {
			await this.$nextTick()
			if (this.leafletMapInternational) return this.leafletMapInternational.invalidateSize()

			const container = this.$refs.mapContainerInternational
			if (!container) return

			const map = L.map(container, { zoomControl: true, attributionControl: false })

			this.leafletMapInternational = map
			const geoLayer = await this.addChoroplethInternational(map)
			const bounds = geoLayer.getBounds()
			map.fitBounds(bounds)
			map.setMinZoom(map.getZoom())
			map.setMaxBounds(bounds)
		},

		async addChoroplethFrance(map) {
			const geojson = france_departements
			const t = this.$t.bind(this)

			const statsMap = {}
			for (const s of this.statsFrance) {
				if (s.dept_code) statsMap[s.dept_code] = s
			}

			const maxTotal = Math.max(1, ...Object.values(statsMap).map(s => s.propale.count + s.signe.count))
			const zeroCA = await Common.priceFormat(0, 'EUR', true)

			const getColor = (pct) => (
				pct > 80 ? '#7f0000'
				: pct > 60 ? '#b30000'
				: pct > 40 ? '#e34a33'
				: pct > 20 ? '#fc8d59'
				: pct > 5 ? '#fdcc8a'
				: pct > 0 ? '#fef0d9'
				: '#f2f2f2'
			)

			const style = (feature) => {
				const s = statsMap[feature.id]
				const pct = s ? Math.round((s.propale.count + s.signe.count) / maxTotal * 100) : 0
				return {
					fillColor: getColor(pct),
					weight: 1,
					opacity: 1,
					color: 'white',
					dashArray: '3',
					fillOpacity: s ? 0.7 : 0.3,
				}
			}

			// Encart d'info (en haut à droite)
			const info = L.control({ position: 'topright' })
			info.onAdd = function () {
				this._div = L.DomUtil.create('div', 'choropleth-info')
				this.update()
				return this._div
			}
			info.update = function (name, s) {
				if (!name) {
					this._div.innerHTML = `<b>${t('cards.widget.contract_map_title')}</b><br><span>${t('cards.widget.contract_map_hover')}</span>`
					return
				}
				const propaleCount = s ? s.propale.count : 0
				const propaleCA = s ? s.propale.ca : zeroCA
				const signeCount = s ? s.signe.count : 0
				const signeCA = s ? s.signe.ca : zeroCA
				this._div.innerHTML = `
					<b>${name}</b>
					<table class="choropleth-table">
						<tr><td>${t('cards.widget.contract_map_propale_count')}</td><td>${propaleCount}</td></tr>
						<tr><td>${t('cards.widget.contract_map_propale_ca')}</td><td>${propaleCA}</td></tr>
						<tr><td>${t('cards.widget.contract_map_signe_count')}</td><td>${signeCount}</td></tr>
						<tr><td>${t('cards.widget.contract_map_signe_ca')}</td><td>${signeCA}</td></tr>
					</table>
				`
			}
			info.addTo(map)

			const highlightFeature = (e) => {
				const layer = e.target
				layer.setStyle({ fillOpacity: 0.95 })
				layer.bringToFront()
				const s = statsMap[layer.feature.id]
				// Affiche le code département à côté du nom, ex : « Calvados (14) »
				info.update(layer.feature.properties.name + ' (' + layer.feature.id + ')', s)
			}

			const resetHighlight = (e) => {
				geoLayer.resetStyle(e.target)
				info.update()
			}

			const onEachFeature = (feature, layer) => {
				layer.on({
					mouseover: highlightFeature,
					mouseout: resetHighlight,
				})
			}

			const geoLayer = L.geoJSON(geojson, { style, onEachFeature }).addTo(map)
			this.geoLayer = geoLayer

			// Légende (en bas à droite)
			const legend = L.control({ position: 'bottomright' })
			legend.onAdd = function () {
				const div = L.DomUtil.create('div', 'choropleth-info choropleth-legend')
				const grades = [0, 5, 20, 40, 60, 80]
				const labels = []
				for (let i = 0; i < grades.length; i++) {
					labels.push(
						`<i style="background:${getColor(grades[i] + 1)}"></i> `
						+ grades[i] + (grades[i + 1] ? '&ndash;' + grades[i + 1] + ' %' : ' %+')
					)
				}
				div.innerHTML = labels.join('<br>')
				return div
			}
			legend.addTo(map)

			return geoLayer
		},

		async addChoroplethInternational(map) {
			const geojson = world_countries
			const t = this.$t.bind(this)

			const statsMap = {}
			for (const s of this.statsInternational) {
				if (s.country_code) statsMap[s.country_code] = s
			}
			// La France (agrégée) est ajoutée pour qu'elle soit colorée sur la carte monde
			if (this.franceInternational) statsMap['FR'] = this.franceInternational

			const maxTotal = Math.max(1, ...Object.values(statsMap).map(s => s.propale.count + s.signe.count))
			const zeroCA = await Common.priceFormat(0, 'EUR', true)

			const getColor = (pct) => (
				pct > 80 ? '#7f0000'
				: pct > 60 ? '#b30000'
				: pct > 40 ? '#e34a33'
				: pct > 20 ? '#fc8d59'
				: pct > 5 ? '#fdcc8a'
				: pct > 0 ? '#fef0d9'
				: '#f2f2f2'
			)

			const style = (feature) => {
				const s = statsMap[feature.id]
				const pct = s ? Math.round((s.propale.count + s.signe.count) / maxTotal * 100) : 0
				return {
					fillColor: getColor(pct),
					weight: 1,
					opacity: 1,
					color: 'white',
					dashArray: '3',
					fillOpacity: s ? 0.7 : 0.3,
				}
			}

			// Encart d'info (en haut à droite)
			const info = L.control({ position: 'topright' })
			info.onAdd = function () {
				this._div = L.DomUtil.create('div', 'choropleth-info')
				this.update()
				return this._div
			}
			info.update = function (name, s) {
				if (!name) {
					this._div.innerHTML = `<b>${t('cards.widget.contract_map_title')}</b><br><span>${t('cards.widget.contract_map_hover_country')}</span>`
					return
				}
				const propaleCount = s ? s.propale.count : 0
				const propaleCA = s ? s.propale.ca : zeroCA
				const signeCount = s ? s.signe.count : 0
				const signeCA = s ? s.signe.ca : zeroCA
				this._div.innerHTML = `
					<b>${name}</b>
					<table class="choropleth-table">
						<tr><td>${t('cards.widget.contract_map_propale_count')}</td><td>${propaleCount}</td></tr>
						<tr><td>${t('cards.widget.contract_map_propale_ca')}</td><td>${propaleCA}</td></tr>
						<tr><td>${t('cards.widget.contract_map_signe_count')}</td><td>${signeCount}</td></tr>
						<tr><td>${t('cards.widget.contract_map_signe_ca')}</td><td>${signeCA}</td></tr>
					</table>
				`
			}
			info.addTo(map)

			const highlightFeature = (e) => {
				const layer = e.target
				layer.setStyle({ fillOpacity: 0.95 })
				layer.bringToFront()
				const s = statsMap[layer.feature.id]
				// Nom du pays dans la langue de l'application (repli sur le nom du GeoJSON)
				const name = this.countryLabelByIso[layer.feature.id] || layer.feature.properties.name
				info.update(name, s)
			}

			const resetHighlight = (e) => {
				geoLayer.resetStyle(e.target)
				info.update()
			}

			const onEachFeature = (feature, layer) => {
				layer.on({
					mouseover: highlightFeature,
					mouseout: resetHighlight,
				})
			}

			const geoLayer = L.geoJSON(geojson, { style, onEachFeature }).addTo(map)
			this.geoLayerInternational = geoLayer

			// Légende (en bas à droite)
			const legend = L.control({ position: 'bottomright' })
			legend.onAdd = function () {
				const div = L.DomUtil.create('div', 'choropleth-info choropleth-legend')
				const grades = [0, 5, 20, 40, 60, 80]
				const labels = []
				for (let i = 0; i < grades.length; i++) {
					labels.push(
						`<i style="background:${getColor(grades[i] + 1)}"></i> `
						+ grades[i] + (grades[i + 1] ? '&ndash;' + grades[i + 1] + ' %' : ' %+')
					)
				}
				div.innerHTML = labels.join('<br>')
				return div
			}
			legend.addTo(map)

			return geoLayer
		},
	},

	components: {
		LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
	},
}
</script>

<style>
.widget-card .leaflet-pane,
.widget-card .leaflet-top,
.widget-card .leaflet-bottom,
.widget-card .leaflet-control-container .leaflet-top {
	z-index: 1;
}

.choropleth-info {
	padding: 8px 12px;
	background: rgba(255, 255, 255, 0.9);
	box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
	border-radius: 5px;
	font-size: 13px;
	line-height: 1.5;
	min-width: 160px;
}

.choropleth-info b {
	font-size: 14px;
	display: block;
	margin-bottom: 2px;
}

.choropleth-info span {
	color: #777;
}

.choropleth-table {
	margin-top: 6px;
	border-collapse: collapse;
	width: 100%;
}

.choropleth-table td {
	padding: 1px 6px 1px 0;
	font-size: 12px;
}

.choropleth-table td:last-child {
	font-weight: 600;
	text-align: right;
	padding-left: 10px;
}

.choropleth-legend {
	line-height: 22px;
}

.choropleth-legend i {
	width: 18px;
	height: 18px;
	float: left;
	margin-right: 6px;
	opacity: 0.8;
	border-radius: 2px;
}
</style>
